import { ApiMethod, deleteRaw, getRaw, postRaw, putRaw } from 'services/api';
import { useRestMutation, useRestQuery } from 'utils/hooks/api';
import { addParams } from 'utils/urlParams';

/*-------------------------------------------------------------------------------*/

export const ORGANIZATION_MEMBERS_URL_RBAC = '/i/organizations/members';
export const ORGANIZATION_MEMBERSHIP_URL_RBAC = '/i/organizations/memberships';
export const ORGANIZATION_ROLES_URL_RBAC = '/i/organizations/roles';
export const ORGANIZATION_ROLES_URL_RBAC_MERGED = '/i/organizations/projectTeamRoles';
export const ORGANIZATION_PROJECTS_URL = '/i/organizations/projects';
export const ORGANIZATION_TEAMS_URL = '/i/teams';
export const SEND_PASSWORD_RESET_URL = `/i/org-mgmt/sendPasswordReset`;
export const PERMISSIONS_URL = '/i/permissions';
export const DISABLE_TWO_FACTOR_AUTHENTICATION_URL = '/settings/accounts/disableTOTP';

/*-------------------------------------------------------------------------------*/

export function fetchOrganizationMembers(query) {
  return getRaw(
    ORGANIZATION_MEMBERS_URL_RBAC,
    query
      ? {
          params: query,
        }
      : undefined,
  );
}

export const useOrganizationMembers = (query, options) =>
  useRestQuery(ORGANIZATION_MEMBERS_URL_RBAC, query, options);

/**
 * useCreateOrganizationMember
 * @param {UseRestMutationOptions} options
 */
export const useCreateOrganizationMember = (options) =>
  useRestMutation(ORGANIZATION_MEMBERS_URL_RBAC, ApiMethod.post, options);

export function createOrganizationMember(member) {
  return postRaw(ORGANIZATION_MEMBERS_URL_RBAC, member);
}

/**
 * useUpdateOrganizationMember
 * @param {string} userId
 * @param {UseRestMutationOptions} options
 */
export const useUpdateOrganizationMember = (userId = '', options) => {
  const url = addParams(ORGANIZATION_MEMBERS_URL_RBAC, { userId });
  return useRestMutation(url, ApiMethod.put, options);
};

export function updateOrganizationMember(member) {
  return putRaw(ORGANIZATION_MEMBERS_URL_RBAC, member, {
    params: { userId: member.userId },
  });
}

/**
 * useUpdateOrganizationMembership
 * @param {string} userId
 * @param {UseRestMutationOptions} options
 */
export const useUpdateOrganizationMembership = (userId, options) => {
  const url = addParams(ORGANIZATION_MEMBERSHIP_URL_RBAC, { userId });
  return useRestMutation(url, ApiMethod.put, options);
};

export function updateOrganizationMembership(member) {
  return putRaw(ORGANIZATION_MEMBERSHIP_URL_RBAC, member, {
    params: { userId: member.userId },
  });
}

/**
 * useRemoveOrganizationMember
 * @param {string} userId
 * @param {UseRestMutationOptions} options
 */
export const useRemoveOrganizationMember = (userId = '', options) => {
  const url = addParams(ORGANIZATION_MEMBERS_URL_RBAC, { userId });
  return useRestMutation(url, ApiMethod.delete, options);
};

export function removeOrganizationMember(member) {
  return deleteRaw(ORGANIZATION_MEMBERS_URL_RBAC, {
    params: { userId: member.userId },
  });
}

export function sendPasswordReset(member) {
  return postRaw(SEND_PASSWORD_RESET_URL, member, {
    params: { userId: member.userId },
  });
}

/**
 * useSendPasswordReset
 * @param {string} userId
 * @param {UseRestMutationOptions} options
 */
export const useSendPasswordReset = (userId, options) => {
  const url = addParams(SEND_PASSWORD_RESET_URL, { userId });
  return useRestMutation(url, ApiMethod.post, options);
};

export function disableTwoFactorAuthentication(member) {
  return postRaw(DISABLE_TWO_FACTOR_AUTHENTICATION_URL, member, {
    params: { userId: member.userId },
  });
}

export function fetchOrganizationProjects() {
  return getRaw(ORGANIZATION_PROJECTS_URL);
}

export const useOrganizationProjects = (params, options) =>
  useRestQuery(ORGANIZATION_PROJECTS_URL, params, options);

export function fetchOrganizationRoles() {
  return getRaw(ORGANIZATION_ROLES_URL_RBAC);
}

export const useOrganizationRoles = (params, options) =>
  useRestQuery(ORGANIZATION_ROLES_URL_RBAC, params, options);

export function createOrganizationRole(role) {
  return postRaw(ORGANIZATION_ROLES_URL_RBAC_MERGED, {
    ...role,
    scope: 'ProjectTeam',
  });
}

export function updateOrganizationRole(role) {
  return putRaw(ORGANIZATION_ROLES_URL_RBAC_MERGED, {
    ...role,
    scope: 'ProjectTeam',
  });
}

export function fetchOrganizationTeams() {
  return getRaw(ORGANIZATION_TEAMS_URL);
}

export const useOrganizationTeams = (params, options) =>
  useRestQuery(ORGANIZATION_TEAMS_URL, params, options);

export function fetchPermissions() {
  return getRaw(PERMISSIONS_URL);
}

export const usePermissions = (params, options) => useRestQuery(PERMISSIONS_URL, params, options);

export function fetchDeliveryTargets() {
  return Promise.resolve({
    data: [
      {
        id: 1,
        name: 'Appy App',
      },
      {
        id: 2,
        name: 'Bleppy Blep',
      },
    ],
  });
}

export function createDeliveryTarget(deliveryTarget) {
  return Promise.resolve({
    data: {
      id: 1,
      name: deliveryTarget.name,
    },
  });
}

export function updateDeliveryTarget(deliveryTarget) {
  return Promise.resolve({
    data: {
      id: 1,
      name: deliveryTarget.name,
    },
  });
}
